import React, { useState, useEffect, useRef } from "react";
import {
  Typography, Grid, Paper, Autocomplete, TextField, Chip,
  InputAdornment, IconButton, Tooltip, Box,
  FormControlLabel, Switch, Button,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  createSpecialistAction,
  updateSpecialistAction,
  updateSpecialistSignatureAction,
} from "../../redux/actions/specialist";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomTextField from "../../form/CustomTextField";
import strings from "../../i18n/language";
import Fab from "@mui/material/Fab";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";

const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghjkmnpqrstuvwxyz";
const DIGITS = "23456789";
const SPECIAL = "!@#$%&*?";

const generatePassword = () => {
  const all = UPPER + LOWER + DIGITS + SPECIAL;
  const mandatory = [
    UPPER[Math.floor(Math.random() * UPPER.length)],
    LOWER[Math.floor(Math.random() * LOWER.length)],
    DIGITS[Math.floor(Math.random() * DIGITS.length)],
    SPECIAL[Math.floor(Math.random() * SPECIAL.length)],
  ];
  const rest = Array.from({ length: 8 }, () => all[Math.floor(Math.random() * all.length)]);
  return [...mandatory, ...rest].sort(() => Math.random() - 0.5).join("");
};

const passwordRules = (pwd) => ({
  length: pwd.length >= 8,
  upper: /[A-Z]/.test(pwd),
  lower: /[a-z]/.test(pwd),
  digit: /[0-9]/.test(pwd),
  special: /[!@#$%&*?]/.test(pwd),
});

const passwordValid = (pwd) => Object.values(passwordRules(pwd)).every(Boolean);

const SpecialistEdit = () => {
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState([]);
  const [name, setName] = useState(null);
  const [phone, setPhone] = useState(null);
  const [mail, setMail] = useState(null);
  const [comment, setComment] = useState(null);
  const [identityNumber, setIdentityNumber] = useState(null);
  const [mpps, setMpps] = useState(null);
  const [password, setPassword] = useState(generatePassword());
  const [medicinaOcupacional, setMedicinaOcupacional] = useState(false);
  const [valid, setValid] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const signatureInputRef = useRef(null);

  const specialist = useSelector((state) => state.specialist.specialist);
  const especialidades = useSelector((state) => state.settings.especialidades || []);
  const dispatch = useDispatch();
  const isNew = !specialist?.id;

  useEffect(() => {
    if (specialist) {
      setSpecialties(
        specialist.specialty
          ? specialist.specialty.split(",").map((s) => s.trim()).filter(Boolean)
          : []
      );
      setName(specialist.displayName);
      setPhone(specialist.phone);
      setMail(specialist.email);
      setComment(specialist.comment);
      setIdentityNumber(specialist.identityNumber || null);
      setMpps(specialist.mpps || null);
      setMedicinaOcupacional(!!specialist.medicinaOcupacional);
      setSignaturePreview(specialist.signature || null);
    }
  }, [specialist]);

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result;
      setSignaturePreview(base64);
      dispatch(updateSpecialistSignatureAction(specialist.id, base64));
    };
    reader.readAsDataURL(file);
  };

  const handleSignatureDelete = () => {
    setSignaturePreview(null);
    dispatch(updateSpecialistSignatureAction(specialist.id, null));
  };

  useEffect(() => {
    const pwdOk = !isNew || passwordValid(password);
    setValid(!!(specialties.length > 0 && name && name.length > 1 && pwdOk));
  }, [specialties, name, password, isNew]);

  const handleCopy = () => {
    navigator.clipboard.writeText(password);
    toast.info("Contraseña copiada");
  };

  const save = () => {
    const specialty = specialties.join(", ");
    if (!isNew) {
      dispatch(updateSpecialistAction({ id: specialist.id, specialty, displayName: name, phone, email: mail, comment, identityNumber, mpps, medicinaOcupacional }));
    } else {
      dispatch(createSpecialistAction({ specialty, displayName: name, phone, email: mail, comment, identityNumber, mpps, password, medicinaOcupacional }));
    }
    navigate("/specialists");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate("/specialists")} sx={{ alignSelf: "flex-start", mb: 1, color: "text.secondary" }}>
        Volver
      </Button>
      <Paper sx={{ p: 2, width: "80%", backgroundColor: "white" }}>
        <Grid container justifyContent="center" margin="normal" mt={2}>
          <Grid item xs={8} mt={2} display="flex">
            <Autocomplete
              multiple
              fullWidth
              options={especialidades}
              value={specialties}
              onChange={(_, newValue) => setSpecialties(newValue)}
              freeSolo
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key} label={option} size="small" {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={strings.LABEL_SPECIALTY}
                  placeholder="Añadir especialidad..."
                  margin="normal"
                />
              )}
            />
          </Grid>
          {specialties.length === 0 && name !== null && (
            <Grid item xs={8} mt={1}>
              <Typography color="error">{strings.REQUIREMENT_SPECIALTY}</Typography>
            </Grid>
          )}
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="name"
              label={strings.LABEL_NAME}
              name="name"
              value={name}
              onChange={setName}
            />
          </Grid>
          {name && (
            <Grid item xs={8} mt={1}>
              <Typography
                color={name && name.length > 1 ? "success.main" : "error"}
              >
                {strings.REQUIREMENT_NAME_SPECIALIST}
              </Typography>
            </Grid>
          )}
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="phone"
              label={strings.LABEL_PHONE}
              name="phone"
              value={phone}
              onChange={setPhone}
            />
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="mail"
              label={strings.LABEL_MAIL}
              name="mail"
              value={mail}
              onChange={setMail}
            />
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="comment"
              label={strings.LABEL_COMMENT}
              name="comment"
              value={comment}
              onChange={setComment}
            />
          </Grid>
          <Grid item xs={8} mt={2}>
            <Box display="flex" gap={2}>
              <CustomTextField
                margin="normal"
                fullWidth
                id="identityNumber"
                label="Cédula (CI)"
                name="identityNumber"
                value={identityNumber}
                onChange={setIdentityNumber}
              />
              <CustomTextField
                margin="normal"
                fullWidth
                id="mpps"
                label="MPPS"
                name="mpps"
                value={mpps}
                onChange={setMpps}
              />
            </Box>
          </Grid>
          <Grid item xs={8} mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={medicinaOcupacional}
                  onChange={(e) => setMedicinaOcupacional(e.target.checked)}
                  color="primary"
                />
              }
              label="Medicina Ocupacional"
            />
            {medicinaOcupacional && (
              <Typography variant="caption" color="text.secondary" display="block">
                Este especialista tendrá acceso a las funcionalidades de medicina ocupacional.
              </Typography>
            )}
          </Grid>
          {!isNew && (
            <Grid item xs={8} mt={2}>
              <Typography variant="subtitle2" gutterBottom>Firma del especialista</Typography>
              {signaturePreview ? (
                <Box display="flex" alignItems="center" gap={2}>
                  <img
                    src={signaturePreview}
                    alt="Firma"
                    style={{ maxHeight: 80, maxWidth: 300, border: "1px solid #ccc", borderRadius: 4, background: "#fff" }}
                  />
                  <Tooltip title="Eliminar firma">
                    <IconButton color="error" onClick={handleSignatureDelete}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">Sin firma registrada</Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => signatureInputRef.current.click()}
              >
                {signaturePreview ? "Cambiar firma" : "Subir firma"}
              </Button>
              <input
                ref={signatureInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleSignatureUpload}
              />
            </Grid>
          )}
          {isNew && (
            <Grid item xs={8} mt={2}>
              <TextField
                fullWidth
                margin="normal"
                label="Contraseña de acceso"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!passwordValid(password)}
                helperText="Generada automáticamente — comunícala al especialista"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Regenerar">
                        <IconButton onClick={() => setPassword(generatePassword())}>
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copiar">
                        <IconButton onClick={handleCopy}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              {!passwordValid(password) && (() => {
                const rules = passwordRules(password);
                return (
                  <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {[
                      { ok: rules.length, label: "Mín. 8 caracteres" },
                      { ok: rules.upper, label: "Mayúscula" },
                      { ok: rules.lower, label: "Minúscula" },
                      { ok: rules.digit, label: "Número" },
                      { ok: rules.special, label: "Carácter especial (!@#$%&*?)" },
                    ].map(({ ok, label }) => (
                      <Typography
                        key={label}
                        variant="caption"
                        sx={{ color: ok ? "success.main" : "error.main" }}
                      >
                        {ok ? "✓" : "✗"} {label}
                      </Typography>
                    ))}
                  </Box>
                );
              })()}
            </Grid>
          )}
        </Grid>
      </Paper>
      <div
        style={{
          alignSelf: "flex-end",
          flexDirection: "column",
          display: "flex",
          marginLeft: 10,
        }}
      >
        <Fab color="error" aria-label="cancel" disabled={true}>
          <CancelIcon />
        </Fab>
        <Fab
          color="primary"
          aria-label="save"
          style={{ marginTop: 10 }}
          onClick={save}
          disabled={!valid}
        >
          <SaveIcon />
        </Fab>
      </div>
    </Box>
  );
};

export default SpecialistEdit;
